/**
 * Demo-mode axios plumbing: the fake adapter that answers registered
 * mutations locally, and the GET merge that layers overlay effects onto
 * real responses. Wired into api.ts's interceptors (request + response).
 *
 * Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
 */
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { demoOverlay } from './overlay'
import type { DemoHandler } from './handlers'

/**
 * Build a per-request axios adapter that resolves with the handler's
 * fabricated body after a short, believable latency (so the UI's loading
 * spinners render). The request NEVER reaches the network.
 */
export function makeDemoAdapter(handler: DemoHandler) {
  return async (
    config: InternalAxiosRequestConfig,
  ): Promise<AxiosResponse> => {
    await new Promise((resolve) => setTimeout(resolve, 400))
    return {
      data: handler.respond(config),
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    } as AxiosResponse
  }
}

/**
 * Serve back the actual file "uploaded" as a demo-fabricated accident's crash
 * report — the ONE GET the demo layer answers locally (api.ts applies this
 * only to /accidents/demo-* /crash-report URLs). A demo- id can only name a
 * fabricated row, so no real report can ever be masked. Unknown id degrades
 * to a small text blob rather than an error mid-demo.
 */
export function makeDemoCrashReportBlobAdapter(url: string) {
  return async (
    config: InternalAxiosRequestConfig,
  ): Promise<AxiosResponse> => {
    await new Promise((resolve) => setTimeout(resolve, 150))
    const m = url.match(/\/v1\/client\/accidents\/(demo-[^/]+)\/crash-report/)
    const file = m?.[1] ? demoOverlay.crashReportFiles[m[1]] : undefined
    return {
      data: file ?? new Blob(['Demo crash report unavailable'], { type: 'text/plain' }),
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    } as AxiosResponse
  }
}

/** Merge overlay effects into GET responses so simulated actions stay visible. */
export function applyDemoGetMerge(response: AxiosResponse): AxiosResponse {
  const url = response.config.url || ''

  // Driver roster list reads — harvest id → full_name so fabricated accidents
  // can render the picked driver's name (handlers.ts). Read-only snoop: the
  // response itself is not modified. Detail GETs (/drivers/{id}) fall through
  // the Array.isArray guard harmlessly.
  if (url.includes('/v1/client/drivers')) {
    const items = response.data?.data?.items
    if (Array.isArray(items)) {
      for (const d of items) {
        if (d?.id && typeof d.full_name === 'string') {
          demoOverlay.driverNames[String(d.id)] = d.full_name
        }
      }
    }
    return response
  }

  // Accident registry — prepend fabricated accidents, bump recordable counts.
  if (
    url.includes('/v1/client/accidents') &&
    !url.includes('/import') &&
    demoOverlay.accidents.length
  ) {
    const data = response.data
    if (data && Array.isArray(data.items)) {
      const recordables = demoOverlay.accidents.filter((a) => a.recordable).length
      data.items = [...demoOverlay.accidents, ...data.items]
      if (typeof data.recordables_past_12_months === 'number') {
        data.recordables_past_12_months += recordables
      }
      if (typeof data.recordables_past_3_years === 'number') {
        data.recordables_past_3_years += recordables
      }
    }
    return response
  }

  // Driver documents status — flip simulated uploads to on-file so the
  // panel's post-upload load() shows the row as complete.
  const statusMatch = url.match(/\/v1\/client\/requests\/documents\/([^/]+)\/status/)
  if (statusMatch) {
    const driverId = statusMatch[1]
    const data = response.data?.data
    if (data && Array.isArray(data.dqf_items)) {
      const uploaded = new Set(
        demoOverlay.uploadedDocs
          .filter((d) => d.driverId === driverId)
          .map((d) => d.fileType),
      )
      if (uploaded.size) {
        let flipped = 0
        for (const item of data.dqf_items) {
          if (
            item.file_type &&
            uploaded.has(item.file_type) &&
            item.status !== 'complete'
          ) {
            item.status = 'complete'
            flipped += 1
          }
        }
        if (typeof data.dqf_complete === 'number') data.dqf_complete += flipped
      }
    }
    return response
  }

  return response
}
